// CANONICAL COPY: installations\smart-td\content\disciplineLimitsTable.js (ruling 321 slice 3c, task list section 739). src\data\disciplineLimitsTable.js is its
// projection, written by scripts\sync-installation.js - EDIT THE CANONICAL. An edit made only to the projection is refused by the cycle.
// DATA ONLY (section 739): lifted verbatim out of src\data\disciplineAnswerLimits.js, which keeps the code and re-exports every name here.
// Until the SME confirms (data\YCP_DISCIPLINE_ANSWER_CLOCKS_2026-09-25.md, the CONFIRMED section),
// this stays false and every consumer must treat the numbers as a draft, exactly as the carrier clocks
// waited for confirmation before going live.
export const CONFIRMED = false;
export const STATUS = 'EXTRACTED-UNCONFIRMED';

// Per agreement, calendar days unless noted. Two directions kept separate (see the .md):
//   CARRIER clocks (deadlines ON the carrier; a miss is a procedural defect, sometimes fatal to the
//     discipline): noticeOfIntentDays, intentMeetingDays, hearingCommencementDays,
//     decisionAfterHearingDays, appealDecisionDays.
//   ORGANIZATION clocks (OUR deadlines; a miss abandons the appeal): appealFilingDays, nonAcceptanceDays,
//     boardInstitutionMonths, expeditedConferenceDays, expeditedDecisionDays.
//   memberProtection: the discipline analogue of default allowance - a carrier miss that voids the
//     discipline or withdraws the charge.
//   appealRoutesToClaimArticle20: true where the discipline appeal runs on the CLAIM clocks
//     (carrierAnswerLimits.js), not a distinct discipline number (CSX Art. 21(c)).
//   appealOfficers (forensic audit 2026-10-16): the officers the agreement says the appeal is taken to "successively", in
//     order, as the text names them; the LAST is the officer whose decision starts nonAcceptanceDays and
//     boardInstitutionMonths. null where the text names no ladder (the appeal ladder then treats one level as the whole).
//   expeditedAppealOfficer / expeditedAppealDays / expeditedNoticeDays: a dismissal handled under an expedited provision
//     is appealed to one officer within expeditedAppealDays, and that officer's decision is final unless notice is given
//     within expeditedNoticeDays (Amtrak Rule 36, 30 and 30).
//   appealClaimRule: where a discipline appeal is FILED as a claim under the claims rule (BN Rule 24 C) - its later
//     steps are that rule's claim clocks (claimRequirements.js).
//   cites: file:line into data\agreements\*, the quote VIO-1 proves.
export const DISCIPLINE_LIMITS = {
  'Amtrak Yardmaster Agreement (eff. Jan 1, 1983; Labor Relations Printout, Nov 1, 2000)': {
    hearingFrom: 'first_knowledge',   // read at source 2026-10-14 (amtrak 909-910): the hearing runs from the supervisor's first knowledge
    noticeOfIntentDays: 30, intentMeetingDays: 7, hearingCommencementDays: 30,
    decisionAfterHearingDays: 30, appealDecisionDays: 30,
    // 2026-10-15 (the how-to build, claimHowTo.js): Rule 36(e) read at source, amtrak 927-929 - "final and binding unless
    // within sixty (60) calendar days thereafter he is notified in writing that the decision is not acceptable.
    // Subsequent handling must be instituted within six (6) months from the date of such decision." Was null here.
    appealFilingDays: 30, nonAcceptanceDays: 60, boardInstitutionMonths: 6,
    expeditedConferenceDays: 30, expeditedDecisionDays: 15,
    // Forensic audit 2026-10-16: Rule 36(d) - "appeal in writing may be taken successively, to the Division Manager-Labor
    // Relations and the Director-Labor Relations, provided such appeals are made in writing within thirty (30) calendar
    // days from the date of previous decision" (amtrak-ym-2000-11-01.txt:922-924); 36(e) runs the 60 days and six months
    // from "The decision of the Director-Labor Relations" (:927-929) - the second officer, not the first.
    appealOfficers: ['Division Manager-Labor Relations', 'Director-Labor Relations'],
    // Expedited Handling of Dismissal Cases: the General Chairman "must, within thirty (30) days after the date of the
    // notice of dismissal, appeal the case directly to the Director-Labor Relations" (:931-932); that decision is "final
    // and binding unless, within thirty (30) days of such decision the General Chairman notifies the Director-Labor
    // Relations of his desire to submit the case to a tribunal" (:940-942). The provision states no institution period.
    expeditedAppealOfficer: 'Director-Labor Relations', expeditedAppealDays: 30, expeditedNoticeDays: 30,
    appealRoutesToClaimArticle20: false,
    memberProtection: 'letter of intent withdrawn and cancelled if management fails to attend the resolution meeting',
    provision: 'DISC-time-limits',
    // What each clock runs FROM, in the rule's words (read at the cites below, 2026-10-15) - for the how-to.
    from: {
      noticeOfIntent: "the supervisor's actual knowledge of the offense",
      decision: 'the close of the hearing',
      appealFiling: 'the date of the previous decision',
      appealDecision: 'the date the appeal is heard',
    },
    cites: {
      nonAcceptance: 'data/agreements/amtrak-ym-2000-11-01.txt:927',
      boardInstitution: 'data/agreements/amtrak-ym-2000-11-01.txt:929',
      noticeOfIntent: 'data/agreements/amtrak-ym-2000-11-01.txt:889',
      intentMeeting: 'data/agreements/amtrak-ym-2000-11-01.txt:894',
      managementNonAttendance: 'data/agreements/amtrak-ym-2000-11-01.txt:897',
      hearing: 'data/agreements/amtrak-ym-2000-11-01.txt:909',
      decision: 'data/agreements/amtrak-ym-2000-11-01.txt:916',
      appealFiling: 'data/agreements/amtrak-ym-2000-11-01.txt:922',
      appealDecision: 'data/agreements/amtrak-ym-2000-11-01.txt:924',
      expedited: 'data/agreements/amtrak-ym-2000-11-01.txt:937',
      appealOfficers: 'data/agreements/amtrak-ym-2000-11-01.txt:922',
      expeditedAppeal: 'data/agreements/amtrak-ym-2000-11-01.txt:931',
      expeditedNotice: 'data/agreements/amtrak-ym-2000-11-01.txt:940',
      abandonment: 'data/agreements/amtrak-ym-2000-11-01.txt:943',
    },
  },
  'Conrail Yardmasters Agreement (eff. July 1, 1978)': {
    hearingFrom: 'first_knowledge',   // read at source 2026-10-14 (conrail 1242-1243): the hearing runs from the supervisor's first knowledge
    noticeOfIntentDays: null, intentMeetingDays: null, hearingCommencementDays: 20,
    decisionAfterHearingDays: 20, appealDecisionDays: null,
    appealFilingDays: 30, nonAcceptanceDays: 60, boardInstitutionMonths: 6,
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    appealRoutesToClaimArticle20: false,
    memberProtection: null,
    provision: 'DISC-time-limits',
    from: { decision: 'the close of the hearing', appealFiling: 'the date of the previous decision' },   // 1247, 1258 (2026-10-15)
    // Forensic audit 2026-10-16: 6-A-2(a) - "appeal in writing may be taken successively, to the Manager-Labor Relations
    // and Senior Director-Labor Relations" (conrail-rya-7-1-78.source.txt:1254-1259); 6-A-2(b) runs the 60 days and six
    // months from "The decision of the Senior Director-Labor Relations" (:1261-1263).
    appealOfficers: ['Manager-Labor Relations', 'Senior Director-Labor Relations'],
    cites: {
      hearing: 'data/agreements/conrail-rya-7-1-78.source.txt:1242',
      decision: 'data/agreements/conrail-rya-7-1-78.source.txt:1247',
      extension: 'data/agreements/conrail-rya-7-1-78.source.txt:1249',
      appealFiling: 'data/agreements/conrail-rya-7-1-78.source.txt:1255',
      appealOfficers: 'data/agreements/conrail-rya-7-1-78.source.txt:1256',
      nonAcceptance: 'data/agreements/conrail-rya-7-1-78.source.txt:1261',
      boardInstitution: 'data/agreements/conrail-rya-7-1-78.source.txt:1262',
      abandonment: 'data/agreements/conrail-rya-7-1-78.source.txt:1265',
    },
    note: 'REPLACES the component table\'s 10/10/15 "Conrail/1997 YM Rule 22" - that rule/year is not in the record; the held 1978 Rule 6-A-1 says twenty and twenty.',
  },
  'Delaware & Hudson / Canadian Pacific Yardmaster Agreement (codified 2007)': {
    noticeOfIntentDays: 10, intentMeetingDays: null, hearingCommencementDays: 10,
    decisionAfterHearingDays: 21, appealDecisionDays: null,
    // dismissal-expedite: GC appeals to the highest appeals officer within 60 days of the decision;
    // ordinary discipline is grieved under Article 14 (the CLAIM clocks).
    appealFilingDays: 60, nonAcceptanceDays: null, boardInstitutionMonths: null,
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    appealRoutesToClaimArticle20: false,
    memberProtection: 'no discipline may be assessed if the investigation is not scheduled to begin within the time limit',
    provision: 'DISC-time-limits',
    // 724-727, 788, 796-797 (read 2026-10-15).
    from: {
      noticeOfIntent: 'the date of the act or occurrence (for dishonesty, criminal offences or letters of complaint, from when the designated Carrier officer becomes aware of it)',
      decision: 'the date the formal investigation is completed',
      appealFiling: 'the date the decision was rendered',
    },
    cites: {
      notice: 'data/agreements/dh-codified-2007.source.txt:724',
      scheduling: 'data/agreements/dh-codified-2007.source.txt:748',
      noDisciplineIfLate: 'data/agreements/dh-codified-2007.source.txt:768',
      decision: 'data/agreements/dh-codified-2007.source.txt:786',
      dismissalExpedite: 'data/agreements/dh-codified-2007.source.txt:796',
    },
    note: 'hearingCommencementDays is the scheduling window (notice mailed -> investigation begins). A second discipline-flavoured block at dh:1780-1869 is NOT wired pending classification (see the .md, SME Q4).',
  },
  'CSX Yardmaster System Agreement (eff. July 1, 2002)': {
    hearingFrom: 'first_knowledge',   // read at source 2026-10-14 (csx 2092): the hearing runs from the supervisor's first knowledge
    noticeOfIntentDays: null, intentMeetingDays: null, hearingCommencementDays: 15,
    decisionAfterHearingDays: 30, appealDecisionDays: null,
    // T5 / D-16 (2026-10-15), read at csx 2098: the transcript is furnished to the Yardmaster, Local Chairman and General
    // Chairman "within twenty (20) calendar days of completion of hearing".
    transcriptDays: 20,
    // Art. 21(c): the appeal runs on Article 20 - the CLAIM clocks (carrierAnswerLimits.js CSX:
    // 75-day appeal to the HDO, 75-day HDO decision, 12-month institution before the NRAB).
    appealFilingDays: null, nonAcceptanceDays: null, boardInstitutionMonths: null,
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    appealRoutesToClaimArticle20: true,
    memberProtection: null,
    provision: 'DISC-hearing-time-limit',
    from: { decision: 'completion of the investigation' },   // csx 2100 (read 2026-10-15)
    cites: {
      hearing: 'data/agreements/csx-2002-system-agreement.source.txt:2092',
      transcript: 'data/agreements/csx-2002-system-agreement.source.txt:2098',
      decision: 'data/agreements/csx-2002-system-agreement.source.txt:2100',
      appealRouting: 'data/agreements/csx-2002-system-agreement.source.txt:2102',
    },
  },
  'Conrail Mechanical Supervisors Agreement (modifications 1994)': {
    // CORRECTED 2026-10-15: this said the base Rule 15 clocks were "NOT in our held record". They are -
    // data\agreements\ConrailMS_base_1994-05-01.ocr.txt:840-869, read at source: "The hearings shall begin within thirty
    // (30) calendar days from the date of the Company's first knowledge of the occurrence or offense" (856-858);
    // "Decision shall be given in writing ... within twenty (20) calendar days after the close of the hearing" (860-863).
    hearingFrom: 'first_knowledge',
    noticeOfIntentDays: null, intentMeetingDays: null, hearingCommencementDays: 30,
    decisionAfterHearingDays: 20, appealDecisionDays: null,
    // Forensic audit 2026-10-16: base Rule 15(E), not modified in 1994 - "The decision of the Senior Director-Labor
    // Relations shall be considered final and binding unless within sixty (60) calendar days thereafter he is notified in
    // writing that the deciaion is not acceptable. Subsequent handling must be instituted with six (6) months from the
    // date of such decision" (ConrailMS_base_1994-05-01.ocr.txt:903-908, OCR spelling kept). Was null here.
    appealFilingDays: 30, nonAcceptanceDays: 60, boardInstitutionMonths: 6,
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    // The 1994 Rule 15(D) takes the appeal "to the Senior Director-Labor Relations" alone (ConrailMS_modifications_1994
    // .ocr.txt:93-95; Section 3, :96-98, eliminated handling with the Manager-Labor Relations) - one level.
    appealOfficers: ['Senior Director-Labor Relations'],
    appealRoutesToClaimArticle20: false,
    memberProtection: null,
    provision: 'DISC-appeal-limits',
    from: { appealFiling: 'the date of the notice of discipline', decision: 'the close of the hearing' },   // 1994 OCR 93-95; base 860-863
    cites: {
      hearing: 'data/agreements/ConrailMS_base_1994-05-01.ocr.txt:856',
      decision: 'data/agreements/ConrailMS_base_1994-05-01.ocr.txt:860',
      appealFiling: 'data/agreements/ConrailMS_modifications_1994.ocr.txt:93',
      appealOfficers: 'data/agreements/ConrailMS_modifications_1994.ocr.txt:94',
      nonAcceptance: 'data/agreements/ConrailMS_base_1994-05-01.ocr.txt:905',
      boardInstitution: 'data/agreements/ConrailMS_base_1994-05-01.ocr.txt:907',
      abandonment: 'data/agreements/ConrailMS_base_1994-05-01.ocr.txt:910',
      expeditedDismissal: 'data/agreements/ConrailMS_modifications_1994.ocr.txt:83',
    },
    note: 'OCR source. appealFilingDays runs from the DATE OF THE NOTICE OF DISCIPLINE (Rule 15(D)), not the previous decision. Hearing/decision clocks from the base 1994 OCR, Rule 15(B) - verify against paper before use.',
  },
  // ADDED 2026-10-15. This module said "no BN Yardmaster agreement is held". It is, and has been:
  // data\agreements\BN_YM_Schedule_1997-05-10.source.txt, Rule 22 Investigations and Discipline, lines 838-1035, read at
  // source. Rule 22 states no appeal clock of its own.
  // CORRECTED, forensic audit 2026-10-16: the appeal clock is in Rule 24, not Rule 22. Rule 24 C - claims "must be filed
  // ... within sixty (60) days from the date of the occurrence on which the claim is based. The date of occurrence in
  // discipline cases is the date the notification of discipline is received." (BN_YM_Schedule_1997-05-10.source.txt:
  // 1061-1064). A discipline appeal is filed as a Rule 24 claim, 60 days from RECEIPT of the notification of discipline;
  // every later step (Rule 24 C's declination, D, H) is a claim clock and lives in claimRequirements.js, not here.
  'BN Yardmaster Agreement': {
    noticeOfIntentDays: 10, intentMeetingDays: null, hearingCommencementDays: 10,
    decisionAfterHearingDays: 15, appealDecisionDays: null,
    appealFilingDays: 60, nonAcceptanceDays: null, boardInstitutionMonths: null,
    appealClaimRule: 'Rule 24',
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    appealRoutesToClaimArticle20: false,
    memberProtection: 'if the formal hearing is not held within the time limits, the employee will not be disciplined, will be paid for all time lost, and no entry will be made in the personal service record',
    provision: 'DISC-time-limits',
    from: {
      noticeOfIntent: 'the date of occurrence, by certified mail (or, when the occurrence is not immediately known to the Carrier, from when it has knowledge)',
      hearing: 'the date of notification - and not less than five days after it',
      decision: 'the date the hearing is concluded',
      appealFiling: 'the date the notification of discipline is received',   // Rule 24 C, 1064
    },
    cites: {
      appealFiling: 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt:1063',
      occurrenceInDiscipline: 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt:1064',
      notice: 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt:860',
      hearing: 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt:875',
      decision: 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt:998',
      noDisciplineIfLate: 'data/agreements/BN_YM_Schedule_1997-05-10.source.txt:970',
    },
  },
  // ADDED 2026-10-15. Metra (Northeast Illinois Regional Commuter Railroad Corp.) - UTU Yardmasters, eff. Jan 1, 1984,
  // revised to Sep 1, 2010; from Bob's FOIA response 26-FOIA-731. Rule 20, data\agreements\metra-nirc-ym-1984-rev2010
  // .source.txt:687-793, read at source.
  // CORRECTED 2026-10-17 (ruling 318 - every version accounted for before one is relied on). The same FOIA response
  // carries the later agreements Metra calls "in effect"; two of them change this entry:
  //   - Rule 20(b) was deleted and replaced by the agreement signed Aug 27, 2014 (effective Sep 1, 2014): the notice,
  //     the investigation and the decision are each TEN days (metra-nirc-ym-wrb-2014-08-27.source.txt:134-138). This
  //     entry said 7 / 7 / 10 from the superseded 2010 text.
  //   - Rule 23(b) was deleted and replaced by the letter of Aug 4, 2026, signed by Metra Labor Relations and the General
  //     Chairman: appeals "including appeals of disciplinary decisions" are taken within sixty (60) calendar days "from
  //     receipt of ... Notice of Discipline in disciplinary cases", and "The Carrier will then have sixty (60) days to
  //     respond to the Organization's appeal" (metra-nirc-ym-letter-2026-08-04-rule23b.source.txt:15-18). The discipline
  //     appeal now runs inside Rule 23, so its later steps (each succeeding officer, Rule 23(c); the Board) are Rule 23's
  //     claim clocks (claimRequirements.js) - appealClaimRule, as BN. This entry said no appeal clock was stated.
  'Metra (NIRC) Yardmaster Agreement (eff. Jan 1, 1984; rev. Sep 1, 2010)': {
    noticeOfIntentDays: 10, intentMeetingDays: null, hearingCommencementDays: 10,
    decisionAfterHearingDays: 10, appealDecisionDays: 60,
    appealFilingDays: 60, nonAcceptanceDays: null, boardInstitutionMonths: null,
    appealClaimRule: 'Rule 23',
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    appealRoutesToClaimArticle20: false,
    memberProtection: 'an employee held out of service contrary to Rule 20(c) is paid for the time held out; if the transcript fails to sustain the specific charges, the employee is exonerated',
    provision: 'DISC-time-limits',
    from: {
      noticeOfIntent: 'when a Carrier Officer having authority to order an investigation has information of the offense',
      hearing: 'the notice',
      decision: 'the investigation',
      appealFiling: 'receipt of the Notice of Discipline',
      appealDecision: "the Organization's appeal",
    },
    cites: {
      notice: 'data/agreements/metra-nirc-ym-wrb-2014-08-27.source.txt:135',
      hearing: 'data/agreements/metra-nirc-ym-wrb-2014-08-27.source.txt:137',
      decision: 'data/agreements/metra-nirc-ym-wrb-2014-08-27.source.txt:138',
      appealFiling: 'data/agreements/metra-nirc-ym-letter-2026-08-04-rule23b.source.txt:15',
      appealDecision: 'data/agreements/metra-nirc-ym-letter-2026-08-04-rule23b.source.txt:18',
      // Forensic audit 2026-10-16: renamed from noDisciplineIfLate - line 712 is the HELD-OUT-OF-SERVICE PAY line (Rule
      // 20(c)), not a no-discipline-if-late protection; the old name claimed a protection the line does not give.
      heldOutPay: 'data/agreements/metra-nirc-ym-1984-rev2010.source.txt:712',
    },
  },
  // ADDED 2026-10-16 with the GO-256 split. Soo Line Railroad - UTU Yardmasters, schedule of rules eff. Nov 1, 2005.
  // Article 23, data\agreements\soo-ym-2005-11-01.source.txt:1542-1590, read at source. 7 / 7 / 10 - its own text, its
  // own cites (Metra's Rule 20(b) is 10 / 10 / 10 since the 2014 agreement; corrected 2026-10-17). Article 23 states no appeal clock of its own; none is filled in.
  'Soo Line Yardmaster Schedule of Rules (eff. Nov 1, 2005)': {
    noticeOfIntentDays: 7, intentMeetingDays: null, hearingCommencementDays: 7,
    decisionAfterHearingDays: 10, appealDecisionDays: null,
    appealFilingDays: null, nonAcceptanceDays: null, boardInstitutionMonths: null,
    expeditedConferenceDays: null, expeditedDecisionDays: null,
    appealRoutesToClaimArticle20: false,
    memberProtection: 'an employee held out of service contrary to Article 23(d) is paid for time lost on the job from which removed, for the time so held out of service',
    provision: 'DISC-time-limits',
    from: {
      noticeOfIntent: 'when a Company Officer having authority to order an investigation has information of the offense',
      hearing: 'the notice',
      decision: 'the investigation',
    },
    cites: {
      notice: 'data/agreements/soo-ym-2005-11-01.source.txt:1561',
      hearing: 'data/agreements/soo-ym-2005-11-01.source.txt:1563',
      decision: 'data/agreements/soo-ym-2005-11-01.source.txt:1564',
      // Forensic audit 2026-10-16: renamed from noDisciplineIfLate - line 1588 is the held-out-of-service PAY line
      // (Article 23(d)), not a no-discipline-if-late protection.
      heldOutPay: 'data/agreements/soo-ym-2005-11-01.source.txt:1588',
    },
  },
};
